import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  rate: number;
}

const SUPPORTED_CURRENCIES: Record<string, CurrencyInfo> = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', rate: 1 },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', rate: 1 },
};

const COUNTRY_TO_CURRENCY: Record<string, string> = {
  // United Kingdom
  GB: 'GBP', UK: 'GBP',
  // Eurozone
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR',
  AT: 'EUR', BE: 'EUR', FI: 'EUR', IE: 'EUR', PT: 'EUR',
  GR: 'EUR', CY: 'EUR', EE: 'EUR', LV: 'EUR', LT: 'EUR',
  LU: 'EUR', MT: 'EUR', SK: 'EUR', SI: 'EUR', HR: 'EUR',
  // USA
  US: 'USD',
};

// Normalize any country value to ISO 2-letter code
const normalizeCountry = (value?: string | null): string | null => {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (!v) return null;

  // Already 2-letter code
  if (v.length === 2) return v.toUpperCase();

  // Common names
  if (v === 'united kingdom' || v === 'great britain' || v === 'britain' || v === 'england' || v === 'scotland' || v === 'wales') return 'GB';
  if (v === 'united states' || v === 'usa' || v === 'america') return 'US';
  if (v === 'germany' || v === 'deutschland') return 'DE';
  if (v === 'france') return 'FR';
  if (v === 'italy' || v === 'italia') return 'IT';
  if (v === 'spain' || v === 'españa') return 'ES';
  if (v === 'netherlands' || v === 'holland') return 'NL';
  if (v === 'ireland') return 'IE';
  if (v === 'portugal') return 'PT';

  return null;
};

// Get currency code from country code
const getCurrencyFromCountry = (countryCode: string | null): string => {
  if (!countryCode) return 'GBP'; // Default to GBP
  return COUNTRY_TO_CURRENCY[countryCode] || 'USD';
};

// Simple browser-based detection (fallback only)
function detectFromBrowser(): { country: string; currency: string } {
  try {
    const locale = navigator?.language || '';
    
    // en-GB is a strong signal for UK
    if (locale.toLowerCase() === 'en-gb') {
      return { country: 'GB', currency: 'GBP' };
    }

    // Check timezone
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Europe/London' || tz === 'Europe/Belfast') {
      return { country: 'GB', currency: 'GBP' };
    }
    
    // EU timezones
    const euTimezones = ['Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Madrid', 'Europe/Amsterdam', 'Europe/Brussels', 'Europe/Vienna', 'Europe/Lisbon', 'Europe/Athens', 'Europe/Helsinki'];
    if (euTimezones.includes(tz)) {
      return { country: 'EU', currency: 'EUR' };
    }

    // Extract region from locale (e.g., "en-GB" → "GB")
    const region = locale.split('-')[1]?.toUpperCase();
    if (region === 'GB' || region === 'UK') {
      return { country: 'GB', currency: 'GBP' };
    }

    // Default to GBP for this UK-focused app
    return { country: 'GB', currency: 'GBP' };
  } catch {
    return { country: 'GB', currency: 'GBP' };
  }
}

// The actual detection function - runs once and is cached
async function detectCurrency(): Promise<{ currency: CurrencyInfo; country: string }> {
  try {
    // Check user's profile country first
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('country, preferred_currency')
        .eq('user_id', user.id)
        .eq('is_primary', true)
        .single();

      if (profile?.country) {
        const normalizedCountry = normalizeCountry(profile.country);
        if (normalizedCountry) {
          const currencyCode = getCurrencyFromCountry(normalizedCountry);
          
          console.log('[Currency] Using profile country:', profile.country, '→', normalizedCountry, '→', currencyCode);
          
          if (SUPPORTED_CURRENCIES[currencyCode]) {
            return {
              currency: SUPPORTED_CURRENCIES[currencyCode],
              country: normalizedCountry
            };
          }
        }
      }
    }

    // Fallback: Browser locale/timezone (only if no profile country)
    const browserResult = detectFromBrowser();
    console.log('[Currency] No profile country, using browser detection:', browserResult);
    
    return {
      currency: SUPPORTED_CURRENCIES[browserResult.currency] || SUPPORTED_CURRENCIES.GBP,
      country: browserResult.country
    };
  } catch (error) {
    console.error('[Currency] Detection failed:', error);
    return {
      currency: SUPPORTED_CURRENCIES.GBP,
      country: 'GB'
    };
  }
}

export function useCurrencyDetection() {
  const queryClient = useQueryClient();
  
  const { data, isLoading } = useQuery({
    queryKey: ['currency-detection'],
    queryFn: detectCurrency,
    staleTime: Infinity, // Never refetch automatically - runs once per session
    gcTime: Infinity, // Never garbage collect
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false, // Don't refetch when components mount
  });

  const currency = data?.currency || SUPPORTED_CURRENCIES.GBP;
  const country = data?.country || 'GB';

  const formatPrice = (price: number): string => {
    return `${currency.symbol}${price.toFixed(2)}`;
  };

  const convertPrice = (usdPrice: number): number => usdPrice;

  const setCurrencyPreference = async (currencyCode: string) => {
    if (!SUPPORTED_CURRENCIES[currencyCode]) return;
    // Update the cache directly
    queryClient.setQueryData(['currency-detection'], {
      currency: SUPPORTED_CURRENCIES[currencyCode],
      country: country
    });
  };

  return {
    currency,
    country,
    isLoading,
    supportedCurrencies: SUPPORTED_CURRENCIES,
    convertPrice,
    formatPrice,
    setCurrencyPreference,
  };
}
